import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentCompany } from '@/lib/api/companies';
import { CompanyOnboardingModal } from './CompanyOnboardingModal';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/store/authStore';

interface CompanyGuardProps {
  children: ReactNode;
}

/**
 * CompanyGuard ensures the user has a company before accessing the app.
 * Shows onboarding modal if no company exists.
 */
export function CompanyGuard({ children }: CompanyGuardProps) {
  const { data: company, isLoading, isFetching, error, refetch } = useCurrentCompany();
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const authLoading = useAuthStore((state) => state.isLoading);

  // Log errors for debugging
  useEffect(() => {
    if (error) {
      console.error('[CompanyGuard] Error loading company:', error);
    }
  }, [error]);

  // Show the loader while auth is still settling or the first company fetch is
  // in flight. Without the auth gate we'd briefly render onboarding/error before
  // the session propagates (the login "Unable to Load Workspace" flash).
  if (authLoading || isLoading || (isFetching && !company)) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-base">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
          <p className="text-content-secondary">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  // Handle error state - only fatal when we have no company data to fall back on.
  // A transient background-refetch error with cached data must not blow away the app.
  if (error && !company) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-base">
        <div className="max-w-md w-full mx-auto p-8">
          <div className="bg-surface-raised rounded-lg shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-content-primary mb-2">
              Unable to Load Workspace
            </h2>
            <p className="text-sm text-content-secondary mb-6">
              We encountered an error while loading your company information. This might be due to
              a connection issue or corrupted session data.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => refetch()}
                className="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                Try Again
              </button>
              <button
                onClick={async () => {
                  try {
                    await authService.logout();
                    logout();
                    navigate('/login');
                  } catch (err) {
                    console.error('Logout error:', err);
                    // Force logout even if API fails
                    logout();
                    localStorage.clear();
                    sessionStorage.clear();
                    navigate('/login');
                  }
                }}
                className="w-full bg-surface-sunken text-content-primary px-4 py-2 rounded-lg hover:bg-surface-raised transition-colors font-medium"
              >
                Log Out and Clear Data
              </button>
            </div>
            <p className="text-xs text-content-tertiary mt-4">
              If this problem persists, try opening the app in an incognito/private window or
              clearing your browser cache.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // User has no company - show onboarding
  // Or if company exists but modal needs to be shown, pass existing data
  if (!company) {
    return (
      <CompanyOnboardingModal
        isOpen={true}
        onComplete={() => refetch()}
        existingCompany={undefined}
      />
    );
  }

  // Check if we should show modal with existing company data
  // This handles edge cases where user has company but needs to complete setup
  const hasIncompleteSetup = company && !company.name;
  if (hasIncompleteSetup) {
    return (
      <CompanyOnboardingModal
        isOpen={true}
        onComplete={() => refetch()}
        existingCompany={company}
      />
    );
  }

  // User has company - render children
  return <>{children}</>;
}
